import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { RowCheckboxComponent, YesNoIconComponent } from '@rocksoft/cdk';
import { RestrictionsProfile } from '@rocksoft/mdm-sdk-v2';

@Component({
  selector: 'ml-mdm-restrictions-settings',
  templateUrl: './restrictions-settings.component.html',
  styleUrls: ['./restrictions-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FaIconComponent, RowCheckboxComponent, YesNoIconComponent],
})
export class RestrictionsSettingsComponent {
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly title = input('Restriction Settings');
  readonly restrictionsProfile = input.required<RestrictionsProfile>();
  readonly editingInput = input(false);
  readonly allowToggle = input(true);

  readonly restrictionsProfileChange = output<RestrictionsProfile>();

  public restrictionsProfileForm: UntypedFormGroup;

  editing = false;

  constructor() {
    this.createForm();

    effect(() => {
      this.editing = this.editingInput();
    });

    effect(() => {
      const restrictionsProfile = this.restrictionsProfile();
      if (restrictionsProfile) {
        this.rebuildForm(restrictionsProfile);
      }
    });
  }

  doneEditing(): boolean {
    if (!this.editing) {
      return true;
    }

    // Trigger validation on newly added bundle ids which aren't set with a value
    this.autonomousSingleAppModePermittedAppIDs.markAllAsTouched();
    this.allowListedAppBundleIDs.markAllAsTouched();
    this.blockedAppBundleIDs.markAllAsTouched();

    if (this.restrictionsProfileForm.status !== 'VALID') {
      return false;
    }

    const result = this.populate();
    this.restrictionsProfileChange.emit(result);
    return true;
  }

  toggleEditing() {
    // Toggle if we are not editing, or if the edited form validates
    if (!this.editing || this.doneEditing()) {
      this.editing = !this.editing;
    }
  }
  // Used to support multiple inputs for autonomousSingleAppModePermittedAppIDs.
  get autonomousSingleAppModePermittedAppIDs(): UntypedFormArray {
    return this.restrictionsProfileForm.get('autonomousSingleAppModePermittedAppIDs') as UntypedFormArray;
  }

  addAutonomousSingleAppModePermittedAppIDs(): void {
    this.autonomousSingleAppModePermittedAppIDs.push(this.formBuilder.control('', [Validators.required]));
  }

  deleteAutonomousSingleAppModePermittedAppID(index: number): void {
    this.autonomousSingleAppModePermittedAppIDs.removeAt(index);
  }

  // Used to support multiple inputs for blockedAppBundleIDs.
  get blockedAppBundleIDs(): UntypedFormArray {
    return this.restrictionsProfileForm.get('blockedAppBundleIDs') as UntypedFormArray;
  }

  addBlockedAppBundleIDs(): void {
    this.blockedAppBundleIDs.push(this.formBuilder.control('', [Validators.required]));
  }

  deleteBlockedAppBundleIDs(index: number): void {
    this.blockedAppBundleIDs.removeAt(index);
  }

  // Used to support multiple inputs for allowListedAppBundleIDs.
  get allowListedAppBundleIDs(): UntypedFormArray {
    return this.restrictionsProfileForm.get('allowListedAppBundleIDs') as UntypedFormArray;
  }

  addAllowListedAppBundleIDs(): void {
    this.allowListedAppBundleIDs.push(this.formBuilder.control('', [Validators.required]));
  }

  deleteAllowListedAppBundleIDs(index: number): void {
    this.allowListedAppBundleIDs.removeAt(index);
  }

  private createForm() {
    this.restrictionsProfileForm = this.formBuilder.group({
      allowAccountModification: true,
      allowActivityContinuation: true,
      allowAddingGameCenterFriends: true,
      allowAirDrop: true,
      allowAirPlayIncomingRequests: true,
      allowAirPrint: true,
      allowAirPrintCredentialsStorage: true,
      allowAirPrintiBeaconDiscovery: true,
      allowAppCellularDataModification: true,
      allowAppClips: true,
      allowAppInstallation: true,
      allowApplePersonalizedAdvertising: true,
      allowAppRemoval: true,
      allowAssistant: true,
      allowAssistantUserGeneratedContent: true,
      allowAssistantWhileLocked: true,
      allowAutoCorrection: true,
      allowAutomaticAppDownloads: true,
      allowAutoUnlock: true,
      allowBluetoothModification: true,
      allowBookstore: true,
      allowBookstoreErotica: true,
      allowCamera: true,
      allowCellularPlanModification: true,
      allowChat: true,
      allowCloudAddressBook: true,
      allowCloudBackup: true,
      allowCloudBookmarks: true,
      allowCloudCalendar: true,
      allowCloudDesktopAndDocuments: true,
      allowCloudDocumentSync: true,
      allowCloudKeychainSync: true,
      allowCloudMail: true,
      allowCloudNotes: true,
      allowCloudPhotoLibrary: true,
      allowCloudPrivateRelay: true,
      allowCloudReminders: true,
      allowContentCaching: true,
      allowContinuousPathKeyboard: true,
      allowDefinitionLookup: true,
      allowDeviceNameModification: true,
      allowDeviceSleep: true,
      allowDiagnosticSubmission: true,
      allowDiagnosticSubmissionModification: true,
      allowDictation: true,
      allowEnablingRestrictions: true,
      allowEnterpriseAppTrust: true,
      allowEnterpriseBookBackup: true,
      allowEnterpriseBookMetadataSync: true,
      allowEraseContentAndSettings: true,
      allowESIMModification: true,
      allowExplicitContent: true,
      allowFilesNetworkDriveAccess: true,
      allowFilesUSBDriveAccess: true,
      allowFindMyDevice: true,
      allowFindMyFriends: true,
      allowFindMyFriendsModification: true,
      allowFingerprintForUnlock: true,
      allowFingerprintModification: true,
      allowGameCenter: true,
      allowGlobalBackgroundFetchWhenRoaming: true,
      allowHostPairing: true,
      allowInAppPurchases: true,
      allowiTunes: true,
      allowiTunesFileSharing: true,
      allowKeyboardShortcuts: true,
      allowListedAppBundleIDs: this.formBuilder.array([]),
      allowLockScreenControlCenter: true,
      allowLockScreenNotificationsView: true,
      allowLockScreenTodayView: true,
      allowManagedAppsCloudSync: true,
      allowManagedToWriteUnmanagedContacts: false,
      allowMultiplayerGaming: true,
      allowMusicService: true,
      allowNews: true,
      allowNFC: true,
      allowNotificationsModification: true,
      allowOpenFromManagedToUnmanaged: true,
      allowOpenFromUnmanagedToManaged: true,
      allowOTAPKIUpdates: true,
      allowPairedWatch: true,
      allowPassbookWhileLocked: true,
      allowPasscodeModification: true,
      allowPasswordAutoFill: true,
      allowPasswordProximityRequests: true,
      allowPasswordSharing: true,
      allowPersonalHotspotModification: true,
      allowPhotoStream: true,
      allowPodcasts: true,
      allowPredictiveKeyboard: true,
      allowProximitySetupToNewDevice: true,
      allowRadioService: true,
      allowRemoteAppPairing: true,
      allowRemoteScreenObservation: true,
      allowSafari: true,
      allowScreenShot: true,
      allowSharedDeviceTemporarySession: true,
      allowSharedStream: true,
      allowSpellCheck: true,
      allowSpotlightInternetResults: true,
      allowSystemAppRemoval: true,
      allowUIAppInstallation: true,
      allowUIConfigurationProfileInstallation: true,
      allowUnmanagedToReadManagedContacts: false,
      allowUnpairedExternalBootToRecovery: false,
      allowUntrustedTLSPrompt: true,
      allowUSBRestrictedMode: true,
      allowVideoConferencing: true,
      allowVoiceDialing: true,
      allowVPNCreation: true,
      allowWallpaperModification: true,
      allowYouTube: true,
      autonomousSingleAppModePermittedAppIDs: this.formBuilder.array([]),
      blockedAppBundleIDs: this.formBuilder.array([]),
      enforcedFingerprintTimeout: 172800,
      enforcedSoftwareUpdateDelay: 30,
      enforcedSoftwareUpdateMajorOSDeferredInstallDelay: 30,
      enforcedSoftwareUpdateMinorOSDeferredInstallDelay: 30,
      enforcedSoftwareUpdateNonOSDeferredInstallDelay: 30,
      forceAirDropUnmanaged: false,
      forceAirPlayIncomingRequestsPairingPassword: false,
      forceAirPlayOutgoingRequestsPairingPassword: false,
      forceAirPrintTrustedTLSRequirement: false,
      forceAssistantProfanityFilter: false,
      forceAuthenticationBeforeAutoFill: false,
      forceAutomaticDateAndTime: false,
      forceClassroomAutomaticallyJoinClasses: false,
      forceClassroomRequestPermissionToLeaveClasses: false,
      forceClassroomUnpromptedAppAndDeviceLock: false,
      forceClassroomUnpromptedScreenObservation: false,
      forceDelayedAppSoftwareUpdates: false,
      forceDelayedMajorSoftwareUpdates: false,
      forceDelayedSoftwareUpdates: false,
      forceEncryptedBackup: false,
      forceITunesStorePasswordEntry: false,
      forceLimitAdTracking: false,
      forceOnDeviceOnlyDictation: false,
      forceOnDeviceOnlyTranslation: false,
      forceWatchWristDetection: false,
      forceWiFiToAllowedNetworksOnly: false,
      forceWiFiPowerOn: false,
      forceWiFiWhitelisting: false,
      ratingApps: 1000,
      ratingMovies: 1000,
      ratingRegion: undefined,
      ratingTVShows: 1000,
      requireManagedPasteboard: false,
      safariAcceptCookies: 2,
      safariAllowAutoFill: true,
      safariAllowJavaScript: true,
      safariAllowPopups: true,
      safariForceFraudWarning: false,

      allowMailPrivacyProtection: true,
    });
  }

  private rebuildForm(restrictionsProfileParam: RestrictionsProfile) {
    const restrictionsProfile: RestrictionsProfile = {
      payloadIdentifier: '',
      payloadType: 'com.apple.applicationaccess',
      payloadVersion: 1,

      allowAccountModification: restrictionsProfileParam.allowAccountModification,
      allowActivityContinuation: restrictionsProfileParam.allowActivityContinuation,
      allowAddingGameCenterFriends: restrictionsProfileParam.allowAddingGameCenterFriends,
      allowAirDrop: restrictionsProfileParam.allowAirDrop,
      allowAirPlayIncomingRequests: restrictionsProfileParam.allowAirPlayIncomingRequests,
      allowAirPrint: restrictionsProfileParam.allowAirPrint,
      allowAirPrintCredentialsStorage: restrictionsProfileParam.allowAirPrintCredentialsStorage,
      allowAirPrintiBeaconDiscovery: restrictionsProfileParam.allowAirPrintiBeaconDiscovery,
      allowAppCellularDataModification: restrictionsProfileParam.allowAppCellularDataModification,
      allowAppClips: restrictionsProfileParam.allowAppClips,
      allowAppInstallation: restrictionsProfileParam.allowAppInstallation,
      allowApplePersonalizedAdvertising: restrictionsProfileParam.allowApplePersonalizedAdvertising,
      allowAppRemoval: restrictionsProfileParam.allowAppRemoval,
      allowAssistant: restrictionsProfileParam.allowAssistant,
      allowAssistantUserGeneratedContent: restrictionsProfileParam.allowAssistantUserGeneratedContent,
      allowAssistantWhileLocked: restrictionsProfileParam.allowAssistantWhileLocked,
      allowAutoCorrection: restrictionsProfileParam.allowAutoCorrection,
      allowAutomaticAppDownloads: restrictionsProfileParam.allowAutomaticAppDownloads,
      allowAutoUnlock: restrictionsProfileParam.allowAutoUnlock,
      allowBluetoothModification: restrictionsProfileParam.allowBluetoothModification,
      allowBookstore: restrictionsProfileParam.allowBookstore,
      allowBookstoreErotica: restrictionsProfileParam.allowBookstoreErotica,
      allowCamera: restrictionsProfileParam.allowCamera,
      allowCellularPlanModification: restrictionsProfileParam.allowCellularPlanModification,
      allowChat: restrictionsProfileParam.allowChat,
      allowCloudAddressBook: restrictionsProfileParam.allowCloudAddressBook,
      allowCloudBackup: restrictionsProfileParam.allowCloudBackup,
      allowCloudBookmarks: restrictionsProfileParam.allowCloudBookmarks,
      allowCloudCalendar: restrictionsProfileParam.allowCloudCalendar,
      allowCloudDesktopAndDocuments: restrictionsProfileParam.allowCloudDesktopAndDocuments,
      allowCloudDocumentSync: restrictionsProfileParam.allowCloudDocumentSync,
      allowCloudKeychainSync: restrictionsProfileParam.allowCloudKeychainSync,
      allowCloudMail: restrictionsProfileParam.allowCloudMail,
      allowCloudNotes: restrictionsProfileParam.allowCloudNotes,
      allowCloudPhotoLibrary: restrictionsProfileParam.allowCloudPhotoLibrary,
      allowCloudPrivateRelay: restrictionsProfileParam.allowCloudPrivateRelay,
      allowCloudReminders: restrictionsProfileParam.allowCloudReminders,
      allowContentCaching: restrictionsProfileParam.allowContentCaching,
      allowContinuousPathKeyboard: restrictionsProfileParam.allowContinuousPathKeyboard,
      allowDefinitionLookup: restrictionsProfileParam.allowDefinitionLookup,
      allowDeviceNameModification: restrictionsProfileParam.allowDeviceNameModification,
      allowDeviceSleep: restrictionsProfileParam.allowDeviceSleep,
      allowDiagnosticSubmission: restrictionsProfileParam.allowDiagnosticSubmission,
      allowDiagnosticSubmissionModification: restrictionsProfileParam.allowDiagnosticSubmissionModification,
      allowDictation: restrictionsProfileParam.allowDictation,
      allowEnablingRestrictions: restrictionsProfileParam.allowEnablingRestrictions,
      allowEnterpriseAppTrust: restrictionsProfileParam.allowEnterpriseAppTrust,
      allowEnterpriseBookBackup: restrictionsProfileParam.allowEnterpriseBookBackup,
      allowEnterpriseBookMetadataSync: restrictionsProfileParam.allowEnterpriseBookMetadataSync,
      allowEraseContentAndSettings: restrictionsProfileParam.allowEraseContentAndSettings,
      allowESIMModification: restrictionsProfileParam.allowESIMModification,
      allowExplicitContent: restrictionsProfileParam.allowExplicitContent,
      allowFilesNetworkDriveAccess: restrictionsProfileParam.allowFilesNetworkDriveAccess,
      allowFilesUSBDriveAccess: restrictionsProfileParam.allowFilesUSBDriveAccess,
      allowFindMyDevice: restrictionsProfileParam.allowFindMyDevice,
      allowFindMyFriends: restrictionsProfileParam.allowFindMyFriends,
      allowFindMyFriendsModification: restrictionsProfileParam.allowFindMyFriendsModification,
      allowFingerprintForUnlock: restrictionsProfileParam.allowFingerprintForUnlock,
      allowFingerprintModification: restrictionsProfileParam.allowFingerprintModification,
      allowGameCenter: restrictionsProfileParam.allowGameCenter,
      allowGlobalBackgroundFetchWhenRoaming: restrictionsProfileParam.allowGlobalBackgroundFetchWhenRoaming,
      allowHostPairing: restrictionsProfileParam.allowHostPairing,
      allowInAppPurchases: restrictionsProfileParam.allowInAppPurchases,
      allowiTunes: restrictionsProfileParam.allowiTunes,
      allowiTunesFileSharing: restrictionsProfileParam.allowiTunesFileSharing,
      allowKeyboardShortcuts: restrictionsProfileParam.allowKeyboardShortcuts,
      allowLockScreenControlCenter: restrictionsProfileParam.allowLockScreenControlCenter,
      allowLockScreenNotificationsView: restrictionsProfileParam.allowLockScreenNotificationsView,
      allowLockScreenTodayView: restrictionsProfileParam.allowLockScreenTodayView,
      allowManagedAppsCloudSync: restrictionsProfileParam.allowManagedAppsCloudSync,
      allowManagedToWriteUnmanagedContacts: restrictionsProfileParam.allowManagedToWriteUnmanagedContacts,
      allowMultiplayerGaming: restrictionsProfileParam.allowMultiplayerGaming,
      allowMusicService: restrictionsProfileParam.allowMusicService,
      allowNews: restrictionsProfileParam.allowNews,
      allowNFC: restrictionsProfileParam.allowNFC,
      allowNotificationsModification: restrictionsProfileParam.allowNotificationsModification,
      allowOpenFromManagedToUnmanaged: restrictionsProfileParam.allowOpenFromManagedToUnmanaged,
      allowOpenFromUnmanagedToManaged: restrictionsProfileParam.allowOpenFromUnmanagedToManaged,
      allowOTAPKIUpdates: restrictionsProfileParam.allowOTAPKIUpdates,
      allowPairedWatch: restrictionsProfileParam.allowPairedWatch,
      allowPassbookWhileLocked: restrictionsProfileParam.allowPassbookWhileLocked,
      allowPasscodeModification: restrictionsProfileParam.allowPasscodeModification,
      allowPasswordAutoFill: restrictionsProfileParam.allowPasswordAutoFill,
      allowPasswordProximityRequests: restrictionsProfileParam.allowPasswordProximityRequests,
      allowPasswordSharing: restrictionsProfileParam.allowPasswordSharing,
      allowPersonalHotspotModification: restrictionsProfileParam.allowPersonalHotspotModification,
      allowPhotoStream: restrictionsProfileParam.allowPhotoStream,
      allowPodcasts: restrictionsProfileParam.allowPodcasts,
      allowPredictiveKeyboard: restrictionsProfileParam.allowPredictiveKeyboard,
      allowProximitySetupToNewDevice: restrictionsProfileParam.allowProximitySetupToNewDevice,
      allowRadioService: restrictionsProfileParam.allowRadioService,
      allowRemoteAppPairing: restrictionsProfileParam.allowRemoteAppPairing,
      allowRemoteScreenObservation: restrictionsProfileParam.allowRemoteScreenObservation,
      allowSafari: restrictionsProfileParam.allowSafari,
      allowScreenShot: restrictionsProfileParam.allowScreenShot,
      allowSharedDeviceTemporarySession: restrictionsProfileParam.allowSharedDeviceTemporarySession,
      allowSharedStream: restrictionsProfileParam.allowSharedStream,
      allowSpellCheck: restrictionsProfileParam.allowSpellCheck,
      allowSpotlightInternetResults: restrictionsProfileParam.allowSpotlightInternetResults,
      allowSystemAppRemoval: restrictionsProfileParam.allowSystemAppRemoval,
      allowUIAppInstallation: restrictionsProfileParam.allowUIAppInstallation,
      allowUIConfigurationProfileInstallation: restrictionsProfileParam.allowUIConfigurationProfileInstallation,
      allowUnmanagedToReadManagedContacts: restrictionsProfileParam.allowUnmanagedToReadManagedContacts,
      allowUnpairedExternalBootToRecovery: restrictionsProfileParam.allowUnpairedExternalBootToRecovery,
      allowUntrustedTLSPrompt: restrictionsProfileParam.allowUntrustedTLSPrompt,
      allowUSBRestrictedMode: restrictionsProfileParam.allowUSBRestrictedMode,
      allowVideoConferencing: restrictionsProfileParam.allowVideoConferencing,
      allowVoiceDialing: restrictionsProfileParam.allowVoiceDialing,
      allowVPNCreation: restrictionsProfileParam.allowVPNCreation,
      allowWallpaperModification: restrictionsProfileParam.allowWallpaperModification,
      enforcedFingerprintTimeout: restrictionsProfileParam.enforcedFingerprintTimeout,
      enforcedSoftwareUpdateDelay: restrictionsProfileParam.enforcedSoftwareUpdateDelay,
      enforcedSoftwareUpdateMajorOSDeferredInstallDelay:
        restrictionsProfileParam.enforcedSoftwareUpdateMajorOSDeferredInstallDelay,
      enforcedSoftwareUpdateMinorOSDeferredInstallDelay:
        restrictionsProfileParam.enforcedSoftwareUpdateMinorOSDeferredInstallDelay,
      enforcedSoftwareUpdateNonOSDeferredInstallDelay:
        restrictionsProfileParam.enforcedSoftwareUpdateNonOSDeferredInstallDelay,
      forceAirDropUnmanaged: restrictionsProfileParam.forceAirDropUnmanaged,
      forceAirPlayIncomingRequestsPairingPassword: restrictionsProfileParam.forceAirPlayIncomingRequestsPairingPassword,
      forceAirPlayOutgoingRequestsPairingPassword: restrictionsProfileParam.forceAirPlayOutgoingRequestsPairingPassword,
      forceAirPrintTrustedTLSRequirement: restrictionsProfileParam.forceAirPrintTrustedTLSRequirement,
      forceAssistantProfanityFilter: restrictionsProfileParam.forceAssistantProfanityFilter,
      forceAuthenticationBeforeAutoFill: restrictionsProfileParam.forceAuthenticationBeforeAutoFill,
      forceAutomaticDateAndTime: restrictionsProfileParam.forceAutomaticDateAndTime,
      forceClassroomAutomaticallyJoinClasses: restrictionsProfileParam.forceClassroomAutomaticallyJoinClasses,
      forceClassroomRequestPermissionToLeaveClasses:
        restrictionsProfileParam.forceClassroomRequestPermissionToLeaveClasses,
      forceClassroomUnpromptedAppAndDeviceLock: restrictionsProfileParam.forceClassroomUnpromptedAppAndDeviceLock,
      forceClassroomUnpromptedScreenObservation: restrictionsProfileParam.forceClassroomUnpromptedScreenObservation,
      forceDelayedAppSoftwareUpdates: restrictionsProfileParam.forceDelayedAppSoftwareUpdates,
      forceDelayedMajorSoftwareUpdates: restrictionsProfileParam.forceDelayedMajorSoftwareUpdates,
      forceDelayedSoftwareUpdates: restrictionsProfileParam.forceDelayedSoftwareUpdates,
      forceEncryptedBackup: restrictionsProfileParam.forceEncryptedBackup,
      forceITunesStorePasswordEntry: restrictionsProfileParam.forceITunesStorePasswordEntry,
      forceLimitAdTracking: restrictionsProfileParam.forceLimitAdTracking,
      forceOnDeviceOnlyDictation: restrictionsProfileParam.forceOnDeviceOnlyDictation,
      forceOnDeviceOnlyTranslation: restrictionsProfileParam.forceOnDeviceOnlyTranslation,
      forceWatchWristDetection: restrictionsProfileParam.forceWatchWristDetection,
      forceWiFiToAllowedNetworksOnly: restrictionsProfileParam.forceWiFiToAllowedNetworksOnly,
      forceWiFiPowerOn: restrictionsProfileParam.forceWiFiPowerOn,
      forceWiFiWhitelisting: restrictionsProfileParam.forceWiFiWhitelisting,
      ratingApps: restrictionsProfileParam.ratingApps,
      ratingMovies: restrictionsProfileParam.ratingMovies,
      ratingRegion: restrictionsProfileParam.ratingRegion,
      ratingTVShows: restrictionsProfileParam.ratingTVShows,
      requireManagedPasteboard: restrictionsProfileParam.requireManagedPasteboard,
      safariAcceptCookies: restrictionsProfileParam.safariAcceptCookies,
      safariAllowAutoFill: restrictionsProfileParam.safariAllowAutoFill,
      safariAllowJavaScript: restrictionsProfileParam.safariAllowJavaScript,
      safariAllowPopups: restrictionsProfileParam.safariAllowPopups,
      safariForceFraudWarning: restrictionsProfileParam.safariForceFraudWarning,

      allowMailPrivacyProtection: restrictionsProfileParam.allowMailPrivacyProtection,
    };
    this.restrictionsProfileForm.reset(restrictionsProfile);

    this.autonomousSingleAppModePermittedAppIDs.clear();
    const restrictionsProfileValue = this.restrictionsProfile();
    if (restrictionsProfileValue.autonomousSingleAppModePermittedAppIDs) {
      for (const autonomousSingleAppModePermittedAppID of restrictionsProfileValue.autonomousSingleAppModePermittedAppIDs) {
        this.autonomousSingleAppModePermittedAppIDs.push(new UntypedFormControl(autonomousSingleAppModePermittedAppID));
      }
    }

    this.blockedAppBundleIDs.clear();
    if (restrictionsProfileValue.blockedAppBundleIDs) {
      for (const blockedAppBundleID of restrictionsProfileValue.blockedAppBundleIDs) {
        this.blockedAppBundleIDs.push(new UntypedFormControl(blockedAppBundleID));
      }
    }

    this.allowListedAppBundleIDs.clear();
    if (restrictionsProfileValue.allowListedAppBundleIDs) {
      for (const allowListedAppBundleID of restrictionsProfileValue.allowListedAppBundleIDs) {
        this.allowListedAppBundleIDs.push(new UntypedFormControl(allowListedAppBundleID));
      }
    }
  }

  private populate(): RestrictionsProfile {
    const restrictionsSetting: RestrictionsProfile = {
      payloadIdentifier: 'com.rcksft.apple.applicationaccess',
      payloadType: 'com.apple.applicationaccess',
      payloadVersion: 1,

      allowAccountModification: this.restrictionsProfileForm.get('allowAccountModification')?.value,
      allowActivityContinuation: this.restrictionsProfileForm.get('allowActivityContinuation')?.value,
      allowAddingGameCenterFriends: this.restrictionsProfileForm.get('allowAddingGameCenterFriends')?.value,
      allowAirDrop: this.restrictionsProfileForm.get('allowAirDrop')?.value,
      allowAirPlayIncomingRequests: this.restrictionsProfileForm.get('allowAirPlayIncomingRequests')?.value,
      allowAirPrint: this.restrictionsProfileForm.get('allowAirPrint')?.value,
      allowAirPrintCredentialsStorage: this.restrictionsProfileForm.get('allowAirPrintCredentialsStorage')?.value,
      allowAirPrintiBeaconDiscovery: this.restrictionsProfileForm.get('allowAirPrintiBeaconDiscovery')?.value,
      allowAppCellularDataModification: this.restrictionsProfileForm.get('allowAppCellularDataModification')?.value,
      allowAppClips: this.restrictionsProfileForm.get('allowAppClips')?.value,
      allowAppInstallation: this.restrictionsProfileForm.get('allowAppInstallation')?.value,
      allowApplePersonalizedAdvertising: this.restrictionsProfileForm.get('allowApplePersonalizedAdvertising')?.value,
      allowAppRemoval: this.restrictionsProfileForm.get('allowAppRemoval')?.value,
      allowAssistant: this.restrictionsProfileForm.get('allowAssistant')?.value,
      allowAssistantUserGeneratedContent: this.restrictionsProfileForm.get('allowAssistantUserGeneratedContent')?.value,
      allowAssistantWhileLocked: this.restrictionsProfileForm.get('allowAssistantWhileLocked')?.value,
      allowAutoCorrection: this.restrictionsProfileForm.get('allowAutoCorrection')?.value,
      allowAutomaticAppDownloads: this.restrictionsProfileForm.get('allowAutomaticAppDownloads')?.value,
      allowAutoUnlock: this.restrictionsProfileForm.get('allowAutoUnlock')?.value,
      allowBluetoothModification: this.restrictionsProfileForm.get('allowBluetoothModification')?.value,
      allowBookstore: this.restrictionsProfileForm.get('allowBookstore')?.value,
      allowBookstoreErotica: this.restrictionsProfileForm.get('allowBookstoreErotica')?.value,
      allowCamera: this.restrictionsProfileForm.get('allowCamera')?.value,
      allowCellularPlanModification: this.restrictionsProfileForm.get('allowCellularPlanModification')?.value,
      allowChat: this.restrictionsProfileForm.get('allowChat')?.value,
      allowCloudAddressBook: this.restrictionsProfileForm.get('allowCloudAddressBook')?.value,
      allowCloudBackup: this.restrictionsProfileForm.get('allowCloudBackup')?.value,
      allowCloudBookmarks: this.restrictionsProfileForm.get('allowCloudBookmarks')?.value,
      allowCloudCalendar: this.restrictionsProfileForm.get('allowCloudCalendar')?.value,
      allowCloudDesktopAndDocuments: this.restrictionsProfileForm.get('allowCloudDesktopAndDocuments')?.value,
      allowCloudDocumentSync: this.restrictionsProfileForm.get('allowCloudDocumentSync')?.value,
      allowCloudKeychainSync: this.restrictionsProfileForm.get('allowCloudKeychainSync')?.value,
      allowCloudMail: this.restrictionsProfileForm.get('allowCloudMail')?.value,
      allowCloudNotes: this.restrictionsProfileForm.get('allowCloudNotes')?.value,
      allowCloudPhotoLibrary: this.restrictionsProfileForm.get('allowCloudPhotoLibrary')?.value,
      allowCloudPrivateRelay: this.restrictionsProfileForm.get('allowCloudPrivateRelay')?.value,
      allowCloudReminders: this.restrictionsProfileForm.get('allowCloudReminders')?.value,
      allowContentCaching: this.restrictionsProfileForm.get('allowContentCaching')?.value,
      allowContinuousPathKeyboard: this.restrictionsProfileForm.get('allowContinuousPathKeyboard')?.value,
      allowDefinitionLookup: this.restrictionsProfileForm.get('allowDefinitionLookup')?.value,
      allowDeviceNameModification: this.restrictionsProfileForm.get('allowDeviceNameModification')?.value,
      allowDeviceSleep: this.restrictionsProfileForm.get('allowDeviceSleep')?.value,
      allowDiagnosticSubmission: this.restrictionsProfileForm.get('allowDiagnosticSubmission')?.value,
      allowDiagnosticSubmissionModification: this.restrictionsProfileForm.get('allowDiagnosticSubmissionModification')
        ?.value,
      allowDictation: this.restrictionsProfileForm.get('allowDictation')?.value,
      allowEnablingRestrictions: this.restrictionsProfileForm.get('allowEnablingRestrictions')?.value,
      allowEnterpriseAppTrust: this.restrictionsProfileForm.get('allowEnterpriseAppTrust')?.value,
      allowEnterpriseBookBackup: this.restrictionsProfileForm.get('allowEnterpriseBookBackup')?.value,
      allowEnterpriseBookMetadataSync: this.restrictionsProfileForm.get('allowEnterpriseBookMetadataSync')?.value,
      allowEraseContentAndSettings: this.restrictionsProfileForm.get('allowEraseContentAndSettings')?.value,
      allowESIMModification: this.restrictionsProfileForm.get('allowESIMModification')?.value,
      allowExplicitContent: this.restrictionsProfileForm.get('allowExplicitContent')?.value,
      allowFilesNetworkDriveAccess: this.restrictionsProfileForm.get('allowFilesNetworkDriveAccess')?.value,
      allowFilesUSBDriveAccess: this.restrictionsProfileForm.get('allowFilesUSBDriveAccess')?.value,
      allowFindMyDevice: this.restrictionsProfileForm.get('allowFindMyDevice')?.value,
      allowFindMyFriends: this.restrictionsProfileForm.get('allowFindMyFriends')?.value,
      allowFindMyFriendsModification: this.restrictionsProfileForm.get('allowFindMyFriendsModification')?.value,
      allowFingerprintForUnlock: this.restrictionsProfileForm.get('allowFingerprintForUnlock')?.value,
      allowFingerprintModification: this.restrictionsProfileForm.get('allowFingerprintModification')?.value,
      allowGameCenter: this.restrictionsProfileForm.get('allowGameCenter')?.value,
      allowGlobalBackgroundFetchWhenRoaming: this.restrictionsProfileForm.get('allowGlobalBackgroundFetchWhenRoaming')
        ?.value,
      allowHostPairing: this.restrictionsProfileForm.get('allowHostPairing')?.value,
      allowInAppPurchases: this.restrictionsProfileForm.get('allowInAppPurchases')?.value,
      allowiTunes: this.restrictionsProfileForm.get('allowiTunes')?.value,
      allowiTunesFileSharing: this.restrictionsProfileForm.get('allowiTunesFileSharing')?.value,
      allowKeyboardShortcuts: this.restrictionsProfileForm.get('allowKeyboardShortcuts')?.value,
      allowListedAppBundleIDs: this.restrictionsProfileForm.get('allowListedAppBundleIDs')?.value,
      allowLockScreenControlCenter: this.restrictionsProfileForm.get('allowLockScreenControlCenter')?.value,
      allowLockScreenNotificationsView: this.restrictionsProfileForm.get('allowLockScreenNotificationsView')?.value,
      allowLockScreenTodayView: this.restrictionsProfileForm.get('allowLockScreenTodayView')?.value,
      allowManagedAppsCloudSync: this.restrictionsProfileForm.get('allowManagedAppsCloudSync')?.value,
      allowManagedToWriteUnmanagedContacts: this.restrictionsProfileForm.get('allowManagedToWriteUnmanagedContacts')
        ?.value,
      allowMultiplayerGaming: this.restrictionsProfileForm.get('allowMultiplayerGaming')?.value,
      allowMusicService: this.restrictionsProfileForm.get('allowMusicService')?.value,
      allowNews: this.restrictionsProfileForm.get('allowNews')?.value,
      allowNFC: this.restrictionsProfileForm.get('allowNFC')?.value,
      allowNotificationsModification: this.restrictionsProfileForm.get('allowNotificationsModification')?.value,
      allowOpenFromManagedToUnmanaged: this.restrictionsProfileForm.get('allowOpenFromManagedToUnmanaged')?.value,
      allowOpenFromUnmanagedToManaged: this.restrictionsProfileForm.get('allowOpenFromUnmanagedToManaged')?.value,
      allowOTAPKIUpdates: this.restrictionsProfileForm.get('allowOTAPKIUpdates')?.value,
      allowPairedWatch: this.restrictionsProfileForm.get('allowPairedWatch')?.value,
      allowPassbookWhileLocked: this.restrictionsProfileForm.get('allowPassbookWhileLocked')?.value,
      allowPasscodeModification: this.restrictionsProfileForm.get('allowPasscodeModification')?.value,
      allowPasswordAutoFill: this.restrictionsProfileForm.get('allowPasswordAutoFill')?.value,
      allowPasswordProximityRequests: this.restrictionsProfileForm.get('allowPasswordProximityRequests')?.value,
      allowPasswordSharing: this.restrictionsProfileForm.get('allowPasswordSharing')?.value,
      allowPersonalHotspotModification: this.restrictionsProfileForm.get('allowPersonalHotspotModification')?.value,
      allowPhotoStream: this.restrictionsProfileForm.get('allowPhotoStream')?.value,
      allowPodcasts: this.restrictionsProfileForm.get('allowPodcasts')?.value,
      allowPredictiveKeyboard: this.restrictionsProfileForm.get('allowPredictiveKeyboard')?.value,
      allowProximitySetupToNewDevice: this.restrictionsProfileForm.get('allowProximitySetupToNewDevice')?.value,
      allowRadioService: this.restrictionsProfileForm.get('allowRadioService')?.value,
      allowRemoteAppPairing: this.restrictionsProfileForm.get('allowRemoteAppPairing')?.value,
      allowRemoteScreenObservation: this.restrictionsProfileForm.get('allowRemoteScreenObservation')?.value,
      allowSafari: this.restrictionsProfileForm.get('allowSafari')?.value,
      allowScreenShot: this.restrictionsProfileForm.get('allowScreenShot')?.value,
      allowSharedDeviceTemporarySession: this.restrictionsProfileForm.get('allowSharedDeviceTemporarySession')?.value,
      allowSharedStream: this.restrictionsProfileForm.get('allowSharedStream')?.value,
      allowSpellCheck: this.restrictionsProfileForm.get('allowSpellCheck')?.value,
      allowSpotlightInternetResults: this.restrictionsProfileForm.get('allowSpotlightInternetResults')?.value,
      allowSystemAppRemoval: this.restrictionsProfileForm.get('allowSystemAppRemoval')?.value,
      allowUIAppInstallation: this.restrictionsProfileForm.get('allowUIAppInstallation')?.value,
      allowUIConfigurationProfileInstallation: this.restrictionsProfileForm.get(
        'allowUIConfigurationProfileInstallation'
      )?.value,
      allowUnmanagedToReadManagedContacts: this.restrictionsProfileForm.get('allowUnmanagedToReadManagedContacts')
        ?.value,
      allowUnpairedExternalBootToRecovery: this.restrictionsProfileForm.get('allowUnpairedExternalBootToRecovery')
        ?.value,
      allowUntrustedTLSPrompt: this.restrictionsProfileForm.get('allowUntrustedTLSPrompt')?.value,
      allowUSBRestrictedMode: this.restrictionsProfileForm.get('allowUSBRestrictedMode')?.value,
      allowVideoConferencing: this.restrictionsProfileForm.get('allowVideoConferencing')?.value,
      allowVoiceDialing: this.restrictionsProfileForm.get('allowVoiceDialing')?.value,
      allowVPNCreation: this.restrictionsProfileForm.get('allowVPNCreation')?.value,
      allowWallpaperModification: this.restrictionsProfileForm.get('allowWallpaperModification')?.value,
      autonomousSingleAppModePermittedAppIDs: this.restrictionsProfileForm.get('autonomousSingleAppModePermittedAppIDs')
        ?.value,
      blockedAppBundleIDs: this.restrictionsProfileForm.get('blockedAppBundleIDs')?.value,
      enforcedFingerprintTimeout: this.restrictionsProfileForm.get('enforcedFingerprintTimeout')?.value,
      enforcedSoftwareUpdateDelay: this.restrictionsProfileForm.get('enforcedSoftwareUpdateDelay')?.value,
      enforcedSoftwareUpdateMajorOSDeferredInstallDelay: this.restrictionsProfileForm.get(
        'enforcedSoftwareUpdateMajorOSDeferredInstallDelay'
      )?.value,
      enforcedSoftwareUpdateMinorOSDeferredInstallDelay: this.restrictionsProfileForm.get(
        'enforcedSoftwareUpdateMinorOSDeferredInstallDelay'
      )?.value,
      enforcedSoftwareUpdateNonOSDeferredInstallDelay: this.restrictionsProfileForm.get(
        'enforcedSoftwareUpdateNonOSDeferredInstallDelay'
      )?.value,
      forceAirDropUnmanaged: this.restrictionsProfileForm.get('forceAirDropUnmanaged')?.value,
      forceAirPlayIncomingRequestsPairingPassword: this.restrictionsProfileForm.get(
        'forceAirPlayIncomingRequestsPairingPassword'
      )?.value,
      forceAirPlayOutgoingRequestsPairingPassword: this.restrictionsProfileForm.get(
        'forceAirPlayOutgoingRequestsPairingPassword'
      )?.value,
      forceAirPrintTrustedTLSRequirement: this.restrictionsProfileForm.get('forceAirPrintTrustedTLSRequirement')?.value,
      forceAssistantProfanityFilter: this.restrictionsProfileForm.get('forceAssistantProfanityFilter')?.value,
      forceAuthenticationBeforeAutoFill: this.restrictionsProfileForm.get('forceAuthenticationBeforeAutoFill')?.value,
      forceAutomaticDateAndTime: this.restrictionsProfileForm.get('forceAutomaticDateAndTime')?.value,
      forceClassroomAutomaticallyJoinClasses: this.restrictionsProfileForm.get('forceClassroomAutomaticallyJoinClasses')
        ?.value,
      forceClassroomRequestPermissionToLeaveClasses: this.restrictionsProfileForm.get(
        'forceClassroomRequestPermissionToLeaveClasses'
      )?.value,
      forceClassroomUnpromptedAppAndDeviceLock: this.restrictionsProfileForm.get(
        'forceClassroomUnpromptedAppAndDeviceLock'
      )?.value,
      forceClassroomUnpromptedScreenObservation: this.restrictionsProfileForm.get(
        'forceClassroomUnpromptedScreenObservation'
      )?.value,
      forceDelayedAppSoftwareUpdates: this.restrictionsProfileForm.get('forceDelayedAppSoftwareUpdates')?.value,
      forceDelayedMajorSoftwareUpdates: this.restrictionsProfileForm.get('forceDelayedMajorSoftwareUpdates')?.value,
      forceDelayedSoftwareUpdates: this.restrictionsProfileForm.get('forceDelayedSoftwareUpdates')?.value,
      forceEncryptedBackup: this.restrictionsProfileForm.get('forceEncryptedBackup')?.value,
      forceITunesStorePasswordEntry: this.restrictionsProfileForm.get('forceITunesStorePasswordEntry')?.value,
      forceLimitAdTracking: this.restrictionsProfileForm.get('forceLimitAdTracking')?.value,
      forceOnDeviceOnlyDictation: this.restrictionsProfileForm.get('forceOnDeviceOnlyDictation')?.value,
      forceOnDeviceOnlyTranslation: this.restrictionsProfileForm.get('forceOnDeviceOnlyTranslation')?.value,
      forceWatchWristDetection: this.restrictionsProfileForm.get('forceWatchWristDetection')?.value,
      forceWiFiToAllowedNetworksOnly: this.restrictionsProfileForm.get('forceWiFiToAllowedNetworksOnly')?.value,
      forceWiFiPowerOn: this.restrictionsProfileForm.get('forceWiFiPowerOn')?.value,
      forceWiFiWhitelisting: this.restrictionsProfileForm.get('forceWiFiWhitelisting')?.value,
      ratingApps: this.restrictionsProfileForm.get('ratingApps')?.value,
      ratingMovies: this.restrictionsProfileForm.get('ratingMovies')?.value,
      ratingRegion: this.restrictionsProfileForm.get('ratingRegion')?.value,
      ratingTVShows: this.restrictionsProfileForm.get('ratingTVShows')?.value,
      requireManagedPasteboard: this.restrictionsProfileForm.get('requireManagedPasteboard')?.value,
      safariAcceptCookies: this.restrictionsProfileForm.get('safariAcceptCookies')?.value,
      safariAllowAutoFill: this.restrictionsProfileForm.get('safariAllowAutoFill')?.value,
      safariAllowJavaScript: this.restrictionsProfileForm.get('safariAllowJavaScript')?.value,
      safariAllowPopups: this.restrictionsProfileForm.get('safariAllowPopups')?.value,
      safariForceFraudWarning: this.restrictionsProfileForm.get('safariForceFraudWarning')?.value,

      allowMailPrivacyProtection: this.restrictionsProfileForm.get('allowMailPrivacyProtection')?.value,
    };

    return restrictionsSetting;
  }
}
